import type { Node } from '@xyflow/react';

export interface WorkflowAreaPadding {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface WorkflowAreaNodeBox {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  bleedTop: number;
  bleedRight: number;
  bleedBottom: number;
  bleedLeft: number;
}

export interface WorkflowAreaBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface WorkflowAreaCollisionUnit {
  id: string;
  bounds: WorkflowAreaBounds;
}

export interface WorkflowAreaOffset {
  x: number;
  y: number;
}

export const WORKFLOW_AREA_FRAME_PADDING: WorkflowAreaPadding = {
  top: 44,
  right: 28,
  bottom: 28,
  left: 28,
};

export const WORKFLOW_AREA_DROP_TARGET_PADDING: WorkflowAreaPadding = {
  top: 28,
  right: 34,
  bottom: 32,
  left: 34,
};

export const WORKFLOW_AREA_MUTUAL_EXCLUSION_GAP = 24;

export const WORKFLOW_AREA_STEP_CONTROL_WIDTH = 292;
export const WORKFLOW_AREA_PAUSED_STEP_CONTROL_WIDTH = 360;
export const WORKFLOW_AREA_STEP_CONTROL_HEIGHT = 156;
export const WORKFLOW_AREA_STEP_CONTROL_SIDE_GAP = 18;
export const WORKFLOW_AREA_STEP_CONTROL_BELOW_GAP = 16;
export const WORKFLOW_AREA_STEP_CONTROL_VISUAL_BLEED = 18;
export const WORKFLOW_AREA_STEP_CONTROL_RESERVED_WIDTH = (
  WORKFLOW_AREA_STEP_CONTROL_SIDE_GAP
  + WORKFLOW_AREA_STEP_CONTROL_WIDTH
  + WORKFLOW_AREA_STEP_CONTROL_VISUAL_BLEED
);

export function getWorkflowAreaNodeVisualBleed(params: {
  type?: Node['type'];
  selected?: boolean;
  data?: unknown;
}): WorkflowAreaPadding {
  const selected = Boolean(params.selected);

  if (params.type === 'template') {
    return {
      top: selected ? 24 : 18,
      // 选中模板后，步骤控制面板固定出现在节点右侧。这个面板不是 React Flow
      // 节点，measured.width 不会包含它，因此必须在工作流区域几何里显式预留。
      right: Math.max(selected ? 56 : 42, WORKFLOW_AREA_STEP_CONTROL_RESERVED_WIDTH),
      bottom: selected ? 24 : 18,
      left: selected ? 156 : 132,
    };
  }

  if (params.type === 'clip') {
    const nodeData = (params.data || {}) as { showWorkflowHandles?: boolean };
    const handleBleed = nodeData.showWorkflowHandles ? 20 : 10;
    const shadowBleed = selected ? 20 : 14;
    return {
      top: shadowBleed,
      right: Math.max(handleBleed, shadowBleed),
      bottom: shadowBleed,
      left: Math.max(handleBleed, shadowBleed),
    };
  }

  const baseBleed = selected ? 18 : 12;
  return {
    top: baseBleed,
    right: baseBleed,
    bottom: baseBleed,
    left: baseBleed,
  };
}

export function buildWorkflowAreaNodeBox(params: {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  type?: Node['type'];
  selected?: boolean;
  data?: unknown;
}): WorkflowAreaNodeBox {
  const bleed = getWorkflowAreaNodeVisualBleed(params);

  return {
    id: params.id,
    x: params.x,
    y: params.y,
    width: params.width,
    height: params.height,
    bleedTop: bleed.top,
    bleedRight: bleed.right,
    bleedBottom: bleed.bottom,
    bleedLeft: bleed.left,
  };
}

export function buildWorkflowAreaStepControlBox(params: {
  nodeBox: WorkflowAreaNodeBox;
  isPaused: boolean;
}): WorkflowAreaNodeBox {
  const { nodeBox, isPaused } = params;
  const width = isPaused
    ? WORKFLOW_AREA_PAUSED_STEP_CONTROL_WIDTH
    : WORKFLOW_AREA_STEP_CONTROL_WIDTH;

  return {
    id: `${nodeBox.id}:step-controls`,
    x: isPaused
      ? nodeBox.x
      : nodeBox.x + nodeBox.width + WORKFLOW_AREA_STEP_CONTROL_SIDE_GAP,
    y: isPaused
      ? nodeBox.y + nodeBox.height + WORKFLOW_AREA_STEP_CONTROL_BELOW_GAP
      : nodeBox.y + 16,
    width,
    height: WORKFLOW_AREA_STEP_CONTROL_HEIGHT,
    bleedTop: WORKFLOW_AREA_STEP_CONTROL_VISUAL_BLEED,
    bleedRight: WORKFLOW_AREA_STEP_CONTROL_VISUAL_BLEED,
    bleedBottom: WORKFLOW_AREA_STEP_CONTROL_VISUAL_BLEED,
    bleedLeft: WORKFLOW_AREA_STEP_CONTROL_VISUAL_BLEED,
  };
}

export function buildWorkflowAreaBoundsFromBoxes(
  boxes: WorkflowAreaNodeBox[],
  padding: WorkflowAreaPadding = WORKFLOW_AREA_FRAME_PADDING,
): WorkflowAreaBounds | null {
  if (boxes.length === 0) {
    return null;
  }

  const minX = Math.min(...boxes.map((box) => box.x - box.bleedLeft));
  const minY = Math.min(...boxes.map((box) => box.y - box.bleedTop));
  const maxX = Math.max(...boxes.map((box) => box.x + box.width + box.bleedRight));
  const maxY = Math.max(...boxes.map((box) => box.y + box.height + box.bleedBottom));

  return {
    x: minX - padding.left,
    y: minY - padding.top,
    width: maxX - minX + padding.left + padding.right,
    height: maxY - minY + padding.top + padding.bottom,
  };
}

export function getWorkflowAreaNodeBoxVisualBounds(box: WorkflowAreaNodeBox): WorkflowAreaBounds {
  return {
    x: box.x - box.bleedLeft,
    y: box.y - box.bleedTop,
    width: box.width + box.bleedLeft + box.bleedRight,
    height: box.height + box.bleedTop + box.bleedBottom,
  };
}

export function doWorkflowAreaBoundsIntersect(left: WorkflowAreaBounds, right: WorkflowAreaBounds): boolean {
  return left.x < right.x + right.width
    && left.x + left.width > right.x
    && left.y < right.y + right.height
    && left.y + left.height > right.y;
}

export function getWorkflowAreaBoundsSeparation(left: WorkflowAreaBounds, right: WorkflowAreaBounds): number {
  const deltaX = Math.max(left.x - (right.x + right.width), right.x - (left.x + left.width), 0);
  const deltaY = Math.max(left.y - (right.y + right.height), right.y - (left.y + left.height), 0);
  return Math.hypot(deltaX, deltaY);
}

function offsetWorkflowAreaBounds(bounds: WorkflowAreaBounds, offset: WorkflowAreaOffset): WorkflowAreaBounds {
  return {
    x: bounds.x + offset.x,
    y: bounds.y + offset.y,
    width: bounds.width,
    height: bounds.height,
  };
}

function resolveWorkflowAreaMutualExclusionOffset(params: {
  sourceBounds: WorkflowAreaBounds;
  targetBounds: WorkflowAreaBounds;
  currentOffset: WorkflowAreaOffset;
  axis: 'x' | 'y';
  direction: -1 | 1;
  gap: number;
}): WorkflowAreaOffset | null {
  const nextOffset = { ...params.currentOffset };

  if (params.axis === 'x') {
    if (params.direction > 0) {
      const currentLeft = params.targetBounds.x + params.currentOffset.x;
      const requiredLeft = params.sourceBounds.x + params.sourceBounds.width + params.gap;
      if (currentLeft >= requiredLeft) {
        return null;
      }
      nextOffset.x += requiredLeft - currentLeft;
    } else {
      const currentRight = params.targetBounds.x + params.targetBounds.width + params.currentOffset.x;
      const requiredRight = params.sourceBounds.x - params.gap;
      if (currentRight <= requiredRight) {
        return null;
      }
      nextOffset.x += requiredRight - currentRight;
    }
  } else if (params.direction > 0) {
    const currentTop = params.targetBounds.y + params.currentOffset.y;
    const requiredTop = params.sourceBounds.y + params.sourceBounds.height + params.gap;
    if (currentTop >= requiredTop) {
      return null;
    }
    nextOffset.y += requiredTop - currentTop;
  } else {
    const currentBottom = params.targetBounds.y + params.targetBounds.height + params.currentOffset.y;
    const requiredBottom = params.sourceBounds.y - params.gap;
    if (currentBottom <= requiredBottom) {
      return null;
    }
    nextOffset.y += requiredBottom - currentBottom;
  }

  return nextOffset;
}

function resolveWorkflowAreaFixedSourceExclusionOffset(params: {
  sourceBounds: WorkflowAreaBounds;
  targetBounds: WorkflowAreaBounds;
  currentOffset: WorkflowAreaOffset;
  gap: number;
}): WorkflowAreaOffset | null {
  const currentTargetBounds = offsetWorkflowAreaBounds(params.targetBounds, params.currentOffset);
  if (!doWorkflowAreaBoundsIntersect(params.sourceBounds, currentTargetBounds)) {
    return null;
  }

  const sourceRight = params.sourceBounds.x + params.sourceBounds.width;
  const sourceBottom = params.sourceBounds.y + params.sourceBounds.height;
  const targetRight = currentTargetBounds.x + currentTargetBounds.width;
  const targetBottom = currentTargetBounds.y + currentTargetBounds.height;
  const overlapX = Math.min(sourceRight, targetRight) - Math.max(params.sourceBounds.x, currentTargetBounds.x);
  const overlapY = Math.min(sourceBottom, targetBottom) - Math.max(params.sourceBounds.y, currentTargetBounds.y);

  if (overlapX <= 0 || overlapY <= 0) {
    return null;
  }

  const sourceCenterX = params.sourceBounds.x + params.sourceBounds.width / 2;
  const sourceCenterY = params.sourceBounds.y + params.sourceBounds.height / 2;
  const targetCenterX = currentTargetBounds.x + currentTargetBounds.width / 2;
  const targetCenterY = currentTargetBounds.y + currentTargetBounds.height / 2;

  if (overlapX <= overlapY) {
    const direction = targetCenterX < sourceCenterX ? -1 : 1;
    return {
      x: params.currentOffset.x + direction * (overlapX + params.gap),
      y: params.currentOffset.y,
    };
  }

  const direction = targetCenterY < sourceCenterY ? -1 : 1;
  return {
    x: params.currentOffset.x,
    y: params.currentOffset.y + direction * (overlapY + params.gap),
  };
}

export function resolveWorkflowAreaMutualExclusionOffsets(params: {
  units: WorkflowAreaCollisionUnit[];
  sourceUnitId: string;
  deltaX: number;
  deltaY: number;
  gap?: number;
}): Map<string, WorkflowAreaOffset> {
  if (params.deltaX === 0 && params.deltaY === 0) {
    return new Map();
  }

  const sourceUnit = params.units.find((unit) => unit.id === params.sourceUnitId);
  if (!sourceUnit) {
    return new Map();
  }

  const axis = Math.abs(params.deltaX) >= Math.abs(params.deltaY) ? 'x' : 'y';
  const direction = (axis === 'x'
    ? (params.deltaX < 0 ? -1 : 1)
    : (params.deltaY < 0 ? -1 : 1)) as -1 | 1;
  const gap = params.gap ?? WORKFLOW_AREA_MUTUAL_EXCLUSION_GAP;
  const unitById = new Map(params.units.map((unit) => [unit.id, unit] as const));
  const offsets = new Map<string, WorkflowAreaOffset>([
    [params.sourceUnitId, { x: params.deltaX, y: params.deltaY }],
  ]);
  const queue: string[] = [params.sourceUnitId];

  while (queue.length > 0) {
    const sourceId = queue.shift();
    if (!sourceId) {
      continue;
    }

    const currentSourceUnit = unitById.get(sourceId);
    if (!currentSourceUnit) {
      continue;
    }

    const sourceOffset = offsets.get(sourceId) ?? { x: 0, y: 0 };
    const sourceBounds = offsetWorkflowAreaBounds(currentSourceUnit.bounds, sourceOffset);

    params.units.forEach((targetUnit) => {
      if (targetUnit.id === sourceId) {
        return;
      }

      const currentTargetOffset = offsets.get(targetUnit.id) ?? { x: 0, y: 0 };
      const currentTargetBounds = offsetWorkflowAreaBounds(targetUnit.bounds, currentTargetOffset);
      if (!doWorkflowAreaBoundsIntersect(sourceBounds, currentTargetBounds)) {
        return;
      }

      const nextTargetOffset = resolveWorkflowAreaMutualExclusionOffset({
        sourceBounds,
        targetBounds: targetUnit.bounds,
        currentOffset: currentTargetOffset,
        axis,
        direction,
        gap,
      });

      if (!nextTargetOffset) {
        return;
      }

      const hasChanged = nextTargetOffset.x !== currentTargetOffset.x
        || nextTargetOffset.y !== currentTargetOffset.y;
      if (!hasChanged) {
        return;
      }

      offsets.set(targetUnit.id, nextTargetOffset);
      queue.push(targetUnit.id);
    });
  }

  return offsets;
}

export function resolveWorkflowAreaFixedSourceExclusionOffsets(params: {
  units: WorkflowAreaCollisionUnit[];
  sourceUnitId: string;
  sourceBounds: WorkflowAreaBounds;
  gap?: number;
}): Map<string, WorkflowAreaOffset> {
  const sourceUnit = params.units.find((unit) => unit.id === params.sourceUnitId);
  if (!sourceUnit) {
    return new Map();
  }

  const gap = params.gap ?? WORKFLOW_AREA_MUTUAL_EXCLUSION_GAP;
  const unitById = new Map(params.units.map((unit) => [unit.id, unit] as const));
  const offsets = new Map<string, WorkflowAreaOffset>();
  const queue: string[] = [params.sourceUnitId];

  while (queue.length > 0) {
    const sourceId = queue.shift();
    if (!sourceId) {
      continue;
    }

    const currentSourceBounds = sourceId === params.sourceUnitId
      ? params.sourceBounds
      : (() => {
          const unit = unitById.get(sourceId);
          if (!unit) {
            return null;
          }
          return offsetWorkflowAreaBounds(unit.bounds, offsets.get(sourceId) ?? { x: 0, y: 0 });
        })();
    if (!currentSourceBounds) {
      continue;
    }

    params.units.forEach((targetUnit) => {
      if (targetUnit.id === sourceId || targetUnit.id === params.sourceUnitId) {
        return;
      }

      const currentTargetOffset = offsets.get(targetUnit.id) ?? { x: 0, y: 0 };
      const nextTargetOffset = resolveWorkflowAreaFixedSourceExclusionOffset({
        sourceBounds: currentSourceBounds,
        targetBounds: targetUnit.bounds,
        currentOffset: currentTargetOffset,
        gap,
      });
      if (!nextTargetOffset) {
        return;
      }

      const hasChanged = nextTargetOffset.x !== currentTargetOffset.x
        || nextTargetOffset.y !== currentTargetOffset.y;
      if (!hasChanged) {
        return;
      }

      offsets.set(targetUnit.id, nextTargetOffset);
      queue.push(targetUnit.id);
    });
  }

  return offsets;
}

function hasWorkflowAreaExpansionBlocker(params: {
  currentBounds: WorkflowAreaBounds | null;
  nextBounds: WorkflowAreaBounds;
  allNodeBoxes: WorkflowAreaNodeBox[];
  ignoredNodeIds: ReadonlySet<string>;
}): boolean {
  return params.allNodeBoxes.some((box) => {
    if (params.ignoredNodeIds.has(box.id)) {
      return false;
    }

    const boxBounds = getWorkflowAreaNodeBoxVisualBounds(box);
    if (params.currentBounds && doWorkflowAreaBoundsIntersect(params.currentBounds, boxBounds)) {
      return false;
    }

    return doWorkflowAreaBoundsIntersect(params.nextBounds, boxBounds);
  });
}

export function resolveWorkflowAreaSpatialNodeIds(params: {
  anchorNodeIds: string[];
  candidateNodeIds: string[];
  nodeBoxById: ReadonlyMap<string, WorkflowAreaNodeBox>;
  allNodeBoxes: Iterable<WorkflowAreaNodeBox>;
  padding?: WorkflowAreaPadding;
}): { nodeIds: string[]; bounds: WorkflowAreaBounds | null } {
  const padding = params.padding ?? WORKFLOW_AREA_FRAME_PADDING;
  const allNodeBoxes = Array.from(params.allNodeBoxes);
  const includedNodeIds = [...new Set(params.anchorNodeIds)]
    .filter((nodeId) => params.nodeBoxById.has(nodeId));

  let currentBounds = buildWorkflowAreaBoundsFromBoxes(
    includedNodeIds
      .map((nodeId) => params.nodeBoxById.get(nodeId))
      .filter((box): box is WorkflowAreaNodeBox => Boolean(box)),
    padding,
  );

  const remainingCandidateIds = [...new Set(params.candidateNodeIds)]
    .filter((nodeId) => !includedNodeIds.includes(nodeId) && params.nodeBoxById.has(nodeId));
  const semanticNodeIdSet = new Set([
    ...includedNodeIds,
    ...remainingCandidateIds,
  ]);

  while (currentBounds && remainingCandidateIds.length > 0) {
    const activeBounds = currentBounds;

    remainingCandidateIds.sort((leftId, rightId) => {
      const leftBounds = getWorkflowAreaNodeBoxVisualBounds(params.nodeBoxById.get(leftId)!);
      const rightBounds = getWorkflowAreaNodeBoxVisualBounds(params.nodeBoxById.get(rightId)!);
      const distanceDiff = getWorkflowAreaBoundsSeparation(activeBounds, leftBounds)
        - getWorkflowAreaBoundsSeparation(activeBounds, rightBounds);

      if (Math.abs(distanceDiff) > 0.001) {
        return distanceDiff;
      }

      if (leftBounds.y !== rightBounds.y) {
        return leftBounds.y - rightBounds.y;
      }
      if (leftBounds.x !== rightBounds.x) {
        return leftBounds.x - rightBounds.x;
      }

      return leftId.localeCompare(rightId);
    });

    let acceptedCandidateId: string | null = null;
    let acceptedBounds: WorkflowAreaBounds | null = null;

    for (const candidateId of remainingCandidateIds) {
      const nextNodeIds = [...includedNodeIds, candidateId];
      const nextBounds = buildWorkflowAreaBoundsFromBoxes(
        nextNodeIds
          .map((nodeId) => params.nodeBoxById.get(nodeId))
          .filter((box): box is WorkflowAreaNodeBox => Boolean(box)),
        padding,
      );

      if (!nextBounds) {
        continue;
      }

      if (hasWorkflowAreaExpansionBlocker({
        currentBounds,
        nextBounds,
        allNodeBoxes,
        ignoredNodeIds: semanticNodeIdSet,
      })) {
        continue;
      }

      acceptedCandidateId = candidateId;
      acceptedBounds = nextBounds;
      break;
    }

    if (!acceptedCandidateId || !acceptedBounds) {
      break;
    }

    includedNodeIds.push(acceptedCandidateId);
    remainingCandidateIds.splice(remainingCandidateIds.indexOf(acceptedCandidateId), 1);
    currentBounds = acceptedBounds;
  }

  return {
    nodeIds: includedNodeIds,
    bounds: currentBounds,
  };
}

export function isWorkflowAreaNodeBoxWithinBounds(
  box: { x: number; y: number; width: number; height: number },
  bounds: WorkflowAreaBounds,
): boolean {
  const fullyContained = box.x >= bounds.x
    && box.y >= bounds.y
    && box.x + box.width <= bounds.x + bounds.width
    && box.y + box.height <= bounds.y + bounds.height;

  if (fullyContained) {
    return true;
  }

  const centerX = box.x + box.width / 2;
  const centerY = box.y + box.height / 2;
  return centerX >= bounds.x
    && centerX <= bounds.x + bounds.width
    && centerY >= bounds.y
    && centerY <= bounds.y + bounds.height;
}
